/* eslint-disable max-classes-per-file */

import { suggestTypes } from './geo'

/* eslint-disable no-console */
const API_URL = 'https://api.mapy.com/v1/'
const TIMEOUT_MS = 5000

// disable retries for now! bojim se ze pokud uzivatel zmeni adresu tak bude race condition... doresit v budoucnu nejaky abort controller
const MAX_RETRIES = 0
const INITIAL_RETRY_DELAY = 1000

// Cache konfigurace
const CACHE_CONFIG = {
  maxSize: 100, // maximální počet položek v cache
  ttl: 1000 * 60 * 60, // TTL 1 hodina v ms
}

class LRUCache {
  constructor(maxSize, ttl) {
    this.maxSize = maxSize
    this.ttl = ttl
    this.cache = new Map()
  }

  get(key) {
    const item = this.cache.get(key)
    if (!item) return null

    // Kontrola TTL
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key)
      return null
    }

    // Přesun na konec (nejnovější)
    this.cache.delete(key)
    this.cache.set(key, item)
    return item.value
  }

  set(key, value) {
    // Odstranění nejstarší položky pokud je cache plná
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }

    this.cache.set(key, {
      value,
      expiresAt: Date.now() + this.ttl,
    })
  }

  clear() {
    this.cache.clear()
  }
}

class MapyError extends Error {
  constructor(message, code, details) {
    super(message)
    this.name = 'MapyError'
    this.code = code
    this.details = details
  }
}

const validateCoordinates = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    throw new MapyError('Neplatné souřadnice', 'INVALID_COORDINATES', {
      lat,
      lng,
    })
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    throw new MapyError(
      'Souřadnice jsou mimo platný rozsah',
      'COORDINATES_OUT_OF_RANGE',
      { lat, lng }
    )
  }
}

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

const shouldRetry = (error) => {
  if (error instanceof MapyError) {
    // Retry pouze pro dočasné chyby
    return ['TIMEOUT', 'HTTP_ERROR'].includes(error.code)
  }
  return false
}

const processParamValue = (value) => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

const addParamsToUrl = (urlParams, params, prefix = '') => {
  Object.entries(params).forEach(([key, value]) => {
    const paramKey = prefix ? `${prefix}[${key}]` : key

    if (value === null || value === undefined) {
      return
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      addParamsToUrl(urlParams, value, paramKey)
    } else {
      urlParams.append(paramKey, processParamValue(value))
    }
  })
}

const executeRequest = async (url, controller) => {
  const response = await fetch(url, {
    signal: controller.signal,
  })

  if (!response.ok) {
    throw new MapyError(
      `HTTP error! status: ${response.status}`,
      'HTTP_ERROR',
      { status: response.status, statusText: response.statusText }
    )
  }

  const data = await response.json()

  if (!data) {
    throw new MapyError('Prázdná odpověď z API', 'EMPTY_RESPONSE')
  }

  return data
}

// Vytvoření instance cache
const requestCache = new LRUCache(CACHE_CONFIG.maxSize, CACHE_CONFIG.ttl)

const generateCacheKey = (path, params) => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key]
      return acc
    }, {})
  return `${path}:${JSON.stringify(sortedParams)}`
}

// console.log('process.env.GATSBY_MAPY_API_KEY', process.env.GATSBY_MAPY_API_KEY)

const smFetch = async (
  path,
  { apiKey = process.env.GATSBY_MAPY_API_KEY, ...params } = {}
) => {
  if (!apiKey) {
    throw new MapyError('Chybí API klíč', 'MISSING_API_KEY')
  }

  console.log('smFetch', path, params)

  // Kontrola cache
  const cacheKey = generateCacheKey(path, params)
  const cachedResult = requestCache.get(cacheKey)
  if (cachedResult) {
    console.log('Cache hit:', cacheKey)
    return cachedResult
  }

  let lastError
  let attempt = 0

  while (attempt <= MAX_RETRIES) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

      try {
        const urlParams = new URLSearchParams()

        addParamsToUrl(urlParams, params)

        urlParams.append('apikey', apiKey)
        urlParams.append('lang', 'cs')
        console.log('urlParams', urlParams)

        const url = `${API_URL}${path}?${urlParams.toString()}`

        console.log('url', url)

        // eslint-disable-next-line no-await-in-loop
        const result = await executeRequest(url, controller)

        // Uložení do cache
        requestCache.set(cacheKey, result)

        return result
      } finally {
        clearTimeout(timeoutId)
      }
    } catch (error) {
      lastError = error

      if (error.name === 'AbortError') {
        lastError = new MapyError('Vypršel časový limit požadavku', 'TIMEOUT')
      }

      if (error instanceof MapyError) {
        lastError = error
      }

      if (!shouldRetry(lastError) || attempt === MAX_RETRIES) {
        break
      }

      const delay = INITIAL_RETRY_DELAY * 2 ** attempt
      console.log(
        `Retry attempt ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`,
        lastError
      )
      // eslint-disable-next-line no-await-in-loop
      await sleep(delay)
      attempt += 1
    }
  }

  console.error('smFetch error:', {
    message: lastError.message,
    stack: lastError.stack,
    path,
    params,
  })

  throw new MapyError('Chyba při komunikaci s Mapy.cz API', 'API_ERROR', {
    originalError: lastError.message,
  })
}

/**
 * Vytvoří debounced verzi funkce, která se spustí až po určité době neaktivity
 * @param {Function} fn - Funkce k obalení
 * @param {number} delay - Zpoždění v milisekundách
 * @returns {Function} Debounced funkce
 */
export const debounce = (fn, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

export const reverseGeocode = async (lat, lng) => {
  validateCoordinates(lat, lng)

  try {
    const data = await smFetch('rgeocode', {
      lat,
      lon: lng,
    })

    console.log('reverseGeocode', data)
    const result = data?.items?.[0]

    // pokud bude problem, tak zkusit prohledat vsechny items, zatim bereme prvni

    if (!result) {
      throw new MapyError('Nenašel se žádný výsledek', 'NO_RESULT')
    }

    return result
  } catch (error) {
    console.error('reverseGeocode error:', {
      message: error.message,
      code: error.code,
      details: error.details,
      coordinates: { lat, lng },
    })
    throw error
  }
}

export const suggest = async (query) => {
  try {
    if (!query || query.length < 3) {
      return []
    }

    const data = await smFetch('suggest', {
      query,
      limit: 5,
      type: suggestTypes.join(','),
      locality: 'cz',
      preferBBox:
        '14.445898639849048,50.101298825824244,14.452802645853467,50.10500110263837',
    })

    console.log('suggest', data)
    const result = data?.items

    // pokud bude problem, tak zkusit prohledat vsechny items, zatim bereme prvni

    if (!result) {
      throw new MapyError('Nenašel se žádný výsledek', 'NO_RESULT')
    }

    return result
  } catch (error) {
    console.error('suggest error:', {
      message: error.message,
      code: error.code,
      details: error.details,
      query,
    })
    throw error
  }
}

export const findRoute = async (from, to, options) => {
  try {
    const data = await smFetch('routing/route', {
      start: `${from.lng},${from.lat}`,
      end: `${to.lng},${to.lat}`,
      ...options,
    })

    // console.log('>>findRoute', data)
    const result = data

    // pokud bude problem, tak zkusit prohledat vsechny items, zatim bereme prvni

    if (!result) {
      throw new MapyError('Nenašel se žádný výsledek', 'NO_RESULT')
    }

    return {
      duration: result.duration,
      length: result.length,
    }
  } catch (error) {
    console.error('findRoute error:', {
      message: error.message,
      code: error.code,
      details: error.details,
      from,
      to,
      options,
    })
    throw error
  }
}
